import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { useSite, useTrans, useTealium } from '@next-nx/hooks'
import { Button, FormikField, FormLayout, Modal, StepBar } from '@next-nx/shared-ui/atoms'
import styles from './FormRichiestaPreventivoModal.module.sass'
import { Form, Formik } from 'formik'
import { province } from 'libs/shared-ui/utils/province'
import {
  BUDGET_MENSILE_PREVISTO_IVA_ESCLUSA,
  BUDGET_MENSILE_PREVISTO_IVA_INCLUSA,
  KM_PERCORSI,
  TIPOLOGIA_VEICOLO,
  TIPO_UTENTE,
  UTENTE_PRIVATO,
  UTENTE_AZIENDA,
  FLOTTA_POTENZIALE_UTENTE_PRIVATO,
} from 'libs/shared-ui/utils/preventivo'
import { isLandingConvenzione, getTimestamp, isLandingCarrozzerie } from '@next-nx/utils'
import { TypDataTracking, LandingType, OffertaType } from '@next-nx/utils/types'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/nextjs'

interface Props {
  open: boolean
  setOpen: (v: boolean) => void
  onSubmit: (status: string | undefined) => void
  mutation: (e: any) => Promise<any>
  utentePrivato?: boolean
  modelloVeicolo?: string
  tipologiaVeicolo?: string
  className?: string
  offerta?: OffertaType
}

const FormRichiestaPreventivoModal = (props: Props) => {
  const {
    open = false,
    setOpen = () => {},
    onSubmit = () => {},
    mutation = () => {},
    utentePrivato = true,
    modelloVeicolo = '',
    tipologiaVeicolo = '',
    className = '',
    offerta,
  } = props

  const t = useTrans()
  const { landing }: { landing: LandingType } = useSelector((state: any) => state?.landing)
  const router = useRouter()
  const locale = router.locale || landing?.siteId.toString()
  const site = useSite(locale)

  const { linkDataLayer, getProdottoData } = useTealium()

  const [step, setStep] = useState(1)
  const steps = [t('Dati personali'), t('Dati veicolo'), t('Conferma')]
  const [privacyContentVisible, setPrivacyContentVisible] = useState({
    privacy1: false,
    privacy2: false,
    privacy3: false,
    privacy4: false,
  })

  const handleTracking = (step: number, data?: TypDataTracking) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}
    const detail_item = { detail_item: [getProdottoData(offerta, offerta?.categoria?.codice)] }

    switch (step) {
      case 1:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'view',
          action_type: 'step1_dati_personali',
          action_timestamp,
          action_effect: 'form_richiesta_preventivo_step1',
          position: 'popup',
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: '-',
          id_carrozzeria: '-',
        }
        break
      case 2:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'view',
          action_type: 'step2_dati_veicolo',
          action_timestamp,
          action_effect: 'form_richiesta_preventivo_step2',
          position: 'popup',
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: '-',
          id_carrozzeria: '-',
        }
        break
      case 3:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'view',
          action_type: 'step3_conferma',
          action_timestamp,
          action_effect: 'form_richiesta_preventivo_step3',
          position: 'popup',
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: '-',
          id_carrozzeria: '-',
        }
        break
      case 4:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'view',
          action_type: 'thankyou_page',
          action_timestamp,
          action_effect: 'form_richiesta_preventivo_typ',
          position: 'popup',
          info: `chi:${data?.tipoUtente
            ?.replace(/\/|\s+/g, '_')
            .replace(/\./g, '')
            .toLowerCase()}-budget:${data?.budget?.replace(/ /g, '_').toLowerCase()}-km:${data?.km
            ?.replace(/ /g, '_')
            .toLowerCase()}-tipologia:${data?.tipologia?.replace(/ /g, '_').toLowerCase()}${
            data?.convenzione && !!Object.keys(data?.convenzione).length
              ? `-convenzione:${data?.convenzione}`
              : ''
          }`,
          id_preventivo: data?.leadId?.replace(/ /g, '_') || 'none',
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: '-',
          id_carrozzeria: '-',
        }
        break
      case 5:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'click',
          action_type: `chiudi_${data?.stepNome?.replace(/ /g, '_').toLowerCase()}`,
          action_timestamp,
          action_effect: `chiudi_form_richiesta_preventivo_step${data?.step}`,
          position: 'popup',
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: '-',
          id_carrozzeria: '-',
        }
        break
      case 6:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'click',
          action_type: `${data?.azione?.replace(/ /g, '_').toLowerCase()}_${data?.stepNome
            ?.replace(/ /g, '_')
            .toLowerCase()}`,
          action_timestamp,
          action_effect: `interazione_form_richiesta_preventivo_step${data?.step}`,
          position: 'popup',
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: '-',
          id_carrozzeria: '-',
        }
        break
      default:
        dlObj
        break
    }
    linkDataLayer(dlObj)
  }

  useEffect(() => {
    setStep(1)
    open && handleTracking(1)
  }, [open])

  const validationSchema = {
    1: yup.object().shape(
      {
        tipoUtente: yup.string().required('Campo obbligatorio').nullable(),
        nome: yup.string().required('Campo obbligatorio').nullable(),
        cognome: yup.string().required('Campo obbligatorio').nullable(),
        codiceFiscale: yup.string().when('tipoUtente', {
          is: (tipoUtente: string) => tipoUtente === UTENTE_PRIVATO,
          then: yup
            .string()
            .length(16, 'La lunghezza del testo deve essere di 16 caratteri')
            .required('Campo obbligatorio')
            .nullable(),
          otherwise: yup
            .string()
            .length(16, 'La lunghezza del testo deve essere di 16 caratteri')
            .nullable(),
        }),
        ragioneSociale: yup.string().when('tipoUtente', {
          is: (tipoUtente: string) => tipoUtente !== UTENTE_PRIVATO,
          then: yup
            .string()
            .min(2, 'Troppo corto')
            .max(50, 'Troppo lungo')
            .required('Campo obbligatorio')
            .nullable(),
          otherwise: yup.string().nullable(),
        }),
        partitaIva: yup.string().when('tipoUtente', {
          is: (tipoUtente: string) => tipoUtente !== UTENTE_PRIVATO,
          then: yup
            .string()
            .matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici')
            .length(11, 'La lunghezza del testo deve essere di 11 caratteri')
            .required('Campo obbligatorio'),
          otherwise: yup
            .string()
            .matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici')
            .length(11, 'La lunghezza del testo deve essere di 11 caratteri')
            .nullable(),
        }),
        email: yup.string().when('telefono', {
          is: (telefono: string) => !telefono || telefono.length === 0,
          then: yup
            .string()
            .email("Il formato dell'email dev'essere valido")
            .required('Campo obbligatorio')
            .nullable(),
          otherwise: yup.string().email("Il formato dell'email dev'essere valido"),
        }),
        telefono: yup.string().when('email', {
          is: (email: string) => !email || email.length === 0,
          then: yup
            .string()
            .matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici')
            .required('Campo obbligatorio'),
          otherwise: yup.string().matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici'),
        }),
        provincia: yup.string().required('Campo obbligatorio').nullable(),
        flottaPotenziale: yup
          .number()
          .integer('Solo numeri interi')
          .positive('Solo numeri positivi')
          .required('Campo obbligatorio')
          .nullable(),
        convenzione: isLandingConvenzione(site)
          ? yup.string().required('Campo obbligatorio').nullable()
          : yup.string().nullable(),
      },
      [['email', 'telefono']]
    ),
    2: yup.object().shape({
      budgetMensilePrevisto: yup.string().nullable(),
      tipologiaVeicolo: yup.string().nullable(),
      kmPercorsi: yup.string().nullable(),
    }),
    3: yup.object().shape({
      descrizioneRichiesta: yup.string().nullable(),
      informativaPrivacy: yup.bool().oneOf([true], 'Obbligatorio'),
      privacy1: yup.bool(),
      privacy2: yup.bool(),
      privacy3: yup.bool(),
      privacy4: yup.bool(),
    }),
  }

  const showError = (formikProps, name) => {
    return !!formikProps.errors[name]
  }

  const initialValues = {
    tipoUtente: utentePrivato ? UTENTE_PRIVATO : UTENTE_AZIENDA,
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    codiceFiscale: '',
    ragioneSociale: '',
    partitaIva: '',
    provincia: '',
    flottaPotenziale: undefined,
    convenzione: null,
    budgetMensilePrevisto: '',
    tipologiaVeicolo: TIPOLOGIA_VEICOLO.find((t) => t === tipologiaVeicolo) || '',
    kmPercorsi: '',
    descrizioneRichiesta: '',
    informativaPrivacy: false,
    privacy1: false,
    privacy2: false,
    privacy3: false,
    privacy4: false,
  }

  const tipoUtenteOptions = TIPO_UTENTE.map((x) => ({ value: x, label: x }))
  const provinciaOptions = province.map((x) => ({ value: x.nome, label: x.nome }))

  const convenzioniOptions =
    isLandingConvenzione(site) && landing?.convenzioni
      ? landing.convenzioni.map((x) => ({
          value: x,
          label: x,
        }))
      : []

  const budgetMensilePrevistoIiOptions = BUDGET_MENSILE_PREVISTO_IVA_INCLUSA.map((x) => ({
    value: x,
    label: x,
  }))
  const budgetMensilePrevistoIeOptions = BUDGET_MENSILE_PREVISTO_IVA_ESCLUSA.map((x) => ({
    value: x,
    label: x,
  }))
  const mediaKmAnnualeOptions = KM_PERCORSI.map((x) => ({ value: x, label: x }))
  const tipologiaVeicoloOptions = TIPOLOGIA_VEICOLO.map((x) => ({ value: x, label: x }))
  const flottaPotenzialeUtentePrivatoOptions = FLOTTA_POTENZIALE_UTENTE_PRIVATO.map((x) => ({
    value: x,
    label: x,
  }))

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema[step]}
      enableReinitialize
      onSubmit={(values, actions) => {
        mutation({
          variables: {
            input: {
              siteId: locale ? parseInt(locale) : 1,
              nome: values?.nome,
              cognome: values?.cognome,
              email: values?.email,
              telefono: values?.telefono,
              provincia: values?.provincia,
              ragioneSociale: values?.ragioneSociale,
              partitaIva: values?.partitaIva,
              codiceFiscale: values?.codiceFiscale,
              flottaPotenziale: values?.flottaPotenziale,
              budgetMensilePrevisto: values?.budgetMensilePrevisto,
              tipologiaVeicolo: values?.tipologiaVeicolo,
              modelloMacchina: modelloVeicolo || '',
              kmPercorsi: values?.kmPercorsi,
              messaggio: values?.descrizioneRichiesta,
              convenzione: values?.convenzione,
              informativaPrivacy: values?.informativaPrivacy,
              privacy1: values?.privacy1,
              privacy2: values?.privacy2,
              privacy3: values?.privacy3,
              privacy4: values?.privacy4,
            },
          },
        })
          ?.then((res) => {
            const status = res?.data?.createRichiestaContatto?.status
            const leadId = res?.data?.createRichiestaContatto?.leadId
            if (status) {
              onSubmit('success')
              const convenzione =
                !!landing && values?.convenzione ? { convenzione: `${values?.convenzione}` } : {}
              handleTracking(4, {
                tipoUtente: values?.tipoUtente,
                budget: values?.budgetMensilePrevisto,
                km: values?.kmPercorsi,
                tipologia: values?.tipologiaVeicolo,
                leadId: leadId,
                ...convenzione,
              })
              actions.resetForm()
            }
          })
          .catch((err) => {
            Sentry.captureException(err)
            onSubmit('error')
            actions.setSubmitting(false)
          })
      }}
    >
      {(props) => (
        <Form>
          <Modal
            open={open}
            setOpen={(v) => {
              if (v) setOpen(v)
              else {
                handleTracking(5, {
                  stepNome: `step${step}_${steps.find((x) => steps.indexOf(x) === step - 1)}`,
                  step: step,
                })
                setStep(1)
                setOpen(false)
              }
            }}
            closeButton
            title={t(
              'Richiedi un preventivo gratuito, un nostro consulente sarà a tua disposizione'
            )}
            stickyButtons
            buttons={
              <>
                <Button
                  variant="ghost"
                  label={step === 1 ? t('Chiudi') : t('Torna indietro')}
                  onClick={() => {
                    if (step === 1) {
                      setOpen(false)
                      handleTracking(5, {
                        stepNome: 'step1_dati_personali',
                        step: step,
                      })
                    } else {
                      setStep(step - 1)
                      handleTracking(step - 1)
                      handleTracking(6, {
                        azione: 'torna_indietro',
                        stepNome: `step${step}_${steps.find((x) => steps.indexOf(x) === step - 1)}`,
                        step: step,
                      })
                    }
                  }}
                  disabled={props.isSubmitting}
                />
                <Button
                  label={step === 3 ? t('Conferma e invia') : t('Prosegui')}
                  labelMobile={step === 3 ? t('Conferma') : undefined}
                  onClick={() => {
                    handleTracking(6, {
                      azione: 'prosegui',
                      stepNome: `step${step}_${steps.find((x) => steps.indexOf(x) === step - 1)}`,
                      step: step,
                    })
                    if (step === 3) {
                      props.handleSubmit()
                    } else {
                      setStep(step + 1)
                      handleTracking(step + 1)
                    }
                  }}
                  disabled={!Object.keys(props.touched).length || !props.isValid}
                  loading={props.isSubmitting}
                />
              </>
            }
            className={`${styles.root} ${className}`}
            bodyClassName={styles.body}
          >
            <div className={styles.head}>
              <StepBar activeStepIndex={step} steps={steps} className={styles.stepBar} />
              <div className={styles.legend}>{t('*Dati obbligatori')}</div>
            </div>
            <div className={styles.form}>
              {step === 1 && (
                <>
                  <FormLayout>
                    {/* Attenzione: stesso div sotto per versione desktop */}
                    {props.values.tipoUtente === UTENTE_PRIVATO && (
                      <div
                        className={`${styles['flottaPotenzialeDisclaimer--mobile']}`}
                        dangerouslySetInnerHTML={{
                          __html: t(
                            '<i>In caso di <strong>flotta potenziale maggiore di 4 veicoli</strong>, compilare il form Business/P.Iva.</i>'
                          ),
                        }}
                      />
                    )}
                    <FormikField
                      formik={props}
                      id="tipoUtente"
                      name="tipoUtente"
                      type="radio"
                      options={tipoUtenteOptions}
                      layout={props.values.tipoUtente === UTENTE_PRIVATO ? undefined : 'full'}
                      required
                      showError={showError}
                      onChange={(e) => {
                        const newTipoUtente = e.target.value
                        props.setFieldValue('tipoUtente', newTipoUtente)
                        if (newTipoUtente === UTENTE_PRIVATO) {
                          props.setFieldValue('partitaIva', '')
                          props.setFieldValue('ragioneSociale', '')
                          props.setFieldValue('flottaPotenziale', undefined)
                        } else {
                          props.setFieldValue('codiceFiscale', '')
                        }
                      }}
                    />
                    {/* Attenzione: stesso div sopra per versione mobile */}
                    {props.values.tipoUtente === UTENTE_PRIVATO && (
                      <div
                        className={styles.flottaPotenzialeDisclaimer}
                        dangerouslySetInnerHTML={{
                          __html: t(
                            '<i>In caso di <strong>flotta potenziale maggiore di 4 veicoli</strong>, compilare il form Business/P.Iva.</i>'
                          ),
                        }}
                      />
                    )}
                    <FormikField
                      formik={props}
                      name="nome"
                      label="Nome"
                      placeholder="Inserisci il tuo nome"
                      required
                      showError={showError}
                    />
                    <FormikField
                      formik={props}
                      name="cognome"
                      label="Cognome"
                      placeholder="Inserisci il tuo cognome"
                      required
                      showError={showError}
                    />
                    {props.values.tipoUtente === UTENTE_PRIVATO ? (
                      <FormikField
                        formik={props}
                        name="codiceFiscale"
                        label="Codice fiscale"
                        placeholder="Inserisci il tuo codice fiscale"
                        required
                        showError={showError}
                      />
                    ) : (
                      <>
                        <FormikField
                          formik={props}
                          name="partitaIva"
                          label="P.Iva"
                          placeholder="Inserisci la partita iva"
                          required
                          showError={showError}
                        />
                        <FormikField
                          formik={props}
                          name="ragioneSociale"
                          label="Ragione sociale"
                          placeholder="Inserisci la ragione sociale"
                          required
                          showError={showError}
                        />
                      </>
                    )}
                    <FormikField
                      formik={props}
                      name="provincia"
                      label="Provincia"
                      placeholder="Seleziona"
                      type="select"
                      options={provinciaOptions}
                      required
                      showError={showError}
                    />
                  </FormLayout>
                  <FormLayout>
                    <FormikField
                      formik={props}
                      label="E-mail"
                      name="email"
                      type="email"
                      placeholder="Inserisci l'email"
                      required
                      showError={showError}
                    />
                    <FormikField
                      formik={props}
                      name="telefono"
                      label="Telefono"
                      placeholder="Inserisci un numero di telefono"
                      required
                      showError={showError}
                    />
                    {props.values.tipoUtente === UTENTE_PRIVATO ? (
                      <FormikField
                        formik={props}
                        name="flottaPotenziale"
                        label="Flotta potenziale"
                        placeholder="Inserisci la flotta potenziale"
                        type="select"
                        options={flottaPotenzialeUtentePrivatoOptions}
                        required
                        showError={showError}
                      />
                    ) : (
                      <FormikField
                        formik={props}
                        name="flottaPotenziale"
                        label="Flotta potenziale"
                        placeholder="Inserisci la flotta potenziale"
                        type="number"
                        min={1}
                        required
                        showError={showError}
                      />
                    )}
                    {isLandingConvenzione(site) && (
                      <FormikField
                        formik={props}
                        name="convenzione"
                        label="Convenzione"
                        placeholder="Seleziona"
                        type="select"
                        options={convenzioniOptions}
                        required
                        showError={showError}
                      />
                    )}
                  </FormLayout>
                </>
              )}
              {step === 2 && (
                <FormLayout>
                  <FormikField
                    formik={props}
                    name="budgetMensilePrevisto"
                    label="Budget mensile previsto (facoltativo)"
                    type="select"
                    options={
                      props.values.tipoUtente === UTENTE_PRIVATO
                        ? budgetMensilePrevistoIiOptions
                        : budgetMensilePrevistoIeOptions
                    }
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    name="kmPercorsi"
                    label="Km percorsi in media annuale (facoltativo)"
                    type="select"
                    options={mediaKmAnnualeOptions}
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    name="tipologiaVeicolo"
                    label="Tipologia veicolo (facoltativo)"
                    type="select"
                    options={tipologiaVeicoloOptions}
                    showError={showError}
                  />
                </FormLayout>
              )}
              {step === 3 && (
                <FormLayout>
                  <FormikField
                    formik={props}
                    name="descrizioneRichiesta"
                    label="Descrizione della richiesta (facoltativo)"
                    placeholder="Inserisci una descrizione della richiesta"
                    type="textarea"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="informativaPrivacy"
                    name="informativaPrivacy"
                    elementLabel={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: t(
                            "Ho preso visione dell'{0}informativa privacy{1} e autorizzo all’invio di comunicazioni inerenti la mia richiesta*",
                            [
                              '<a href="https://www.unipolrental.it/privacy-policy" target="_blank">',
                              '</a>',
                            ]
                          ),
                        }}
                      />
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy1"
                    name="privacy1"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento al trattamento dei miei dati personali per finalità commerciali e di marketing da parte di UnipolRental'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy1'] ? (
                          <>
                            {t(
                              'Invio di comunicazioni commerciali relative a servizi e/o prodotti di UnipolRental (basate anche sull’utilizzo del veicolo e sui dati rilevati dal/i Dispositivo/i se installato/i), nonché relative ad ulteriori prodotti e/o servizi di UnipolSai, delle altre società del Gruppo Unipol e/o di terzi, attraverso materiale pubblicitario, newsletter, vendita diretta, compimento di ricerche di mercato o di comunicazione commerciale da parte di UnipolRental mediante modalità di contatto automatizzate (come SMS, MMS, fax, chiamate telefoniche automatizzate, posta elettronica, messaggi su applicazioni web) e tradizionali (come posta cartacea e chiamate telefoniche con operatore)'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy1: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy1: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy2"
                    name="privacy2"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento al trattamento dei miei dati personali per finalità di profilazione da parte di UnipolRental'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy2'] ? (
                          <>
                            {t(
                              'Attività di analisi, compiute da UnipolRental, dei dati acquisiti all’inizio e nel corso dei rapporti contrattuali con UnipolRental, anche con riguardo ai servizi e prodotti fruiti, basate anche sull’utilizzo del/i veicolo/i - ove disponibili valutazione di alcuni dati legati alla localizzazione, alle percorrenze, agli stili di guida associabili al veicolo assicurato e alle aree geografiche attraversate -, attraverso anche elaborazioni elettroniche, di comportamenti ed esigenze dell’Interessato per migliorare e sviluppare i servizi forniti ed individuare le offerte più vantaggiose ed i prodotti di maggior interesse dell’Interessato stesso'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy2: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy2: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy3"
                    name="privacy3"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento all’invio dei miei dati personali per finalità di marketing a Società del Gruppo Unipol'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy3'] ? (
                          <>
                            {t(
                              'Comunicazione dei dati personali alle altre Società del Gruppo Unipol, che li potranno trattare per l’invio di comunicazioni commerciali relative a servizi assicurativi, nonché a ulteriori prodotti e/o servizi di dette Società e/o di terzi (basate anche sull’utilizzo del veicolo e sui dati rilevati dal/i Dispositivo/i se installato/i), attraverso l’invio di materiale pubblicitario, newsletter, vendita diretta, compimento di ricerche di mercato o di comunicazione commerciale mediante tecniche di comunicazione a distanza con modalità automatizzate (come SMS, MMS, fax, chiamate telefoniche automatizzate, posta elettronica, messaggi su applicazioni web), nonché tradizionali (come posta cartacea e chiamate telefoniche con operatore)'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy3: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy3: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy4"
                    name="privacy4"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento all’invio dei miei dati personali per finalità di profilazione a Società del Gruppo Unipol'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy4'] ? (
                          <>
                            {t(
                              'Comunicazione dei dati personali da UnipolRental alle altre società del Gruppo Unipol, che li utilizzeranno per ulteriori attività di analisi, anche attraverso elaborazioni elettroniche, di comportamenti ed esigenze dell’Interessato, basate sulla valutazione di alcuni dati legati appunto alle percorrenze aggregate e/o puntuali (ove disponibili), alle aree geografiche attraversate, alle esigenze e alle abitudini, dell’Interessato stesso, al fine di migliorare e sviluppare i servizi ed i prodotti forniti ed individuare le offerte più vantaggiose e proporre, sulla base di tali dati, i prodotti ed i servizi di maggiore interesse per l’Interessato'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy4: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy4: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                </FormLayout>
              )}
            </div>
          </Modal>
        </Form>
      )}
    </Formik>
  )
}

export default FormRichiestaPreventivoModal
